"use client";

import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import countriesConfig from "@/countries.config.json" with { type: "json" };
import { getServerConfig } from "@/lib/server/actions/server-config";
import type { Brand, CountryConfig } from "@/utils/middleware.utils";

interface PreviewToolbarProps {
  country: string;
  lang: string;
  siteUid: string;
}

const selectClassName =
  "cursor-pointer rounded border border-white/30 bg-gray-900 px-1.5 py-0.5 text-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50";

export function getBrandMarkets() {
  const { brand } = getServerConfig();
  const brandConfig =
    (countriesConfig as CountryConfig)[brand.value as Brand] ?? {};

  return Object.entries(brandConfig)
    .map(([country, cfg]) => ({
      country,
      defaultLang: cfg.defaultLang,
      availableLangs: cfg.availableLangs,
      siteUid: cfg.siteUid,
    }))
    .sort((a, b) => {
      if (a.country === "global") {
        return -1;
      }
      if (b.country === "global") {
        return 1;
      }
      return a.country.localeCompare(b.country);
    });
}

const PREVIEW_MARKET_KEY = "preview:market";

export function PreviewToolbar({
  country,
  lang,
  siteUid,
}: Readonly<PreviewToolbarProps>) {
  const pathname = usePathname();
  const [isNavigating, setIsNavigating] = useState(false);
  const [isEmbedded, setIsEmbedded] = useState(false);

  useEffect(() => {
    setIsEmbedded(window.self !== window.top);
  }, []);

  const markets = getBrandMarkets();

  useEffect(() => {
    const saved = sessionStorage.getItem(PREVIEW_MARKET_KEY);
    if (!saved || saved === country) {
      return;
    }
    const market = markets.find((m) => m.country === saved);
    if (!market) {
      sessionStorage.removeItem(PREVIEW_MARKET_KEY);
      return;
    }
    // If the Contentful-requested language is not available in the saved market,
    // locale takes priority — clear the saved market and stay on the Contentful URL.
    if (!market.availableLangs.includes(lang)) {
      sessionStorage.removeItem(PREVIEW_MARKET_KEY);
      return;
    }
    const segments = pathname.split("/");
    segments[1] = saved;
    segments[2] = lang;
    setIsNavigating(true);
    window.location.href = segments.join("/");
  }, [country, lang, markets, pathname]);

  function navigate(newPath: string) {
    setIsNavigating(true);
    window.location.href = newPath;
  }

  function switchMarket(newCountry: string) {
    const market = markets.find((m) => m.country === newCountry);

    if (!market) {
      return;
    }

    sessionStorage.setItem(PREVIEW_MARKET_KEY, newCountry);
    const segments = pathname.split("/");
    segments[1] = newCountry;
    segments[2] = market.defaultLang;
    navigate(segments.join("/"));
  }

  const currentMarket = markets.find((m) => m.country === country);
  const otherLangs =
    currentMarket?.availableLangs.filter((l) => l !== lang) ?? [];

  return (
    <div className="fixed top-0 right-0 left-0 z-9998 flex items-center gap-5 border-yellow-400/50 border-b bg-gray-950/95 px-4 py-2 font-mono text-white text-xs backdrop-blur">
      <span className="font-bold text-yellow-400 tracking-widest">PREVIEW</span>

      {isNavigating && (
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
      )}

      <label className="flex items-center gap-1.5">
        <span className="text-gray-400">Market:</span>
        <select
          className={selectClassName}
          disabled={isNavigating}
          onChange={(e) => switchMarket(e.target.value)}
          value={country}
        >
          {markets.map((m) => (
            <option key={m.country} value={m.country}>
              {m.country.toUpperCase()}
            </option>
          ))}
        </select>
      </label>

      <span>
        <span className="text-gray-400">Language: </span>
        {lang}
        {otherLangs.length > 0 && (
          <span className="text-gray-500">
            {" "}
            (more languages: {otherLangs.join(", ")})
          </span>
        )}
      </span>

      <span>
        <span className="text-gray-400">Base Store: </span>
        {siteUid.toUpperCase()}
      </span>

      {!isEmbedded && (
        <a
          className="ml-auto rounded border border-white/30 px-3 py-1 text-white/90 transition-colors hover:border-white hover:bg-white hover:text-gray-900"
          href={`/api/disable-draft?redirect=${encodeURIComponent(pathname)}`}
          onClick={() => sessionStorage.removeItem(PREVIEW_MARKET_KEY)}
        >
          Exit Preview
        </a>
      )}
    </div>
  );
}
